import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import TopBar from "../global/TopBar";
import { MagnifyingGlassIcon, StarIcon, HeartIcon } from "@heroicons/react/20/solid";
import { motion, AnimatePresence } from "framer-motion";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { useSelector } from 'react-redux';
const cities = ["Newbury", "Leeds", "London", "Manchester", "Bristol"];

const Food = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState(cities[0]);
  const [foodItems, setFoodItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);

  const fetchDishes = async (query, city) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_FOOD_API_URL}/dishes?food=${query}&city=${city}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Api-Name": "food-api",
            "Authorization": `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setFoodItems(data.dishes);
    } catch (error) {
      console.error("Error fetching dishes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    fetchDishes(searchQuery, selectedCity);
    const searchState = { searchQuery, selectedCity };
    sessionStorage.setItem('foodSearchState', JSON.stringify(searchState));
    navigate('.', { state: { fromSearch: true, ...searchState }, replace: true });
  };

  useEffect(() => {
    if (location.state?.fromSearch) {
      const { searchQuery: savedQuery, selectedCity: savedCity } = location.state;
      setSearchQuery(savedQuery);
      setSelectedCity(savedCity);
      fetchDishes(savedQuery, savedCity);
    } else {
      sessionStorage.removeItem('foodSearchState');
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      <TopBar />

      <main className="relative">
        {/* Hero Section - Adjusted text size for mobile */}
        <AnimatePresence>
          {!foodItems.length && !isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative px-3 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-8 sm:pb-12 text-center max-w-7xl mx-auto"
            >
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-3xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-4 sm:mb-8"
              >
                <span className="inline-block bg-gradient-to-r from-[#FFD700] to-[#98FB98] text-transparent bg-clip-text">
                  Delicious Food
                </span>
                <br />
                <span className="inline-block text-[#98FB98] mt-1 sm:mt-2 text-2xl sm:text-6xl">
                  Delivered to Your Door
                </span>
              </motion.h1>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Section with Location Menu */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ 
            y: 0, 
            opacity: 1,
            marginTop: foodItems.length ? '1rem' : '0'
          }}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mx-auto px-3 sm:px-4"
        >
          <form onSubmit={handleSearch} className="space-y-3 sm:space-y-4">
            <div className="flex gap-2">
              {/* Location Button - Mobile */}
              <div className="relative sm:hidden">
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsLocationMenuOpen(!isLocationMenuOpen)}
                  className="h-12 px-3 rounded-full bg-white/10 backdrop-blur-xl
                           border border-white/20 text-white
                           focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50
                           transition-all duration-300 flex items-center"
                >
                  <MapPinIcon className="w-5 h-5" />
                </motion.button>

                {/* Location Dropdown - Mobile */}
                <AnimatePresence>
                  {isLocationMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-14 left-0 w-48 py-2 bg-[#1a1f2e]/95 backdrop-blur-xl
                               rounded-xl border border-white/10 shadow-xl z-50"
                    >
                      {cities.map((city) => (
                        <motion.button
                          key={city}
                          type="button"
                          whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          onClick={() => {
                            setSelectedCity(city);
                            setIsLocationMenuOpen(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm transition-colors
                                    ${selectedCity === city 
                                      ? 'text-[#FFD700] bg-white/10' 
                                      : 'text-white'}`}
                        >
                          {city}
                        </motion.button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Search Input */}
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for food..."
                  className="w-full h-12 sm:h-14 px-4 sm:px-6 pr-12 sm:pr-16 text-sm sm:text-base
                           rounded-full bg-white/10 backdrop-blur-xl
                           border border-white/20 text-white placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-[#FFD700]/50
                           transition-all duration-300"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 sm:right-2 top-1.5 sm:top-2 p-2 sm:p-2.5 rounded-full
                           bg-gradient-to-r from-[#FFD700] to-[#98FB98]
                           text-[#1a1f2e] hover:shadow-lg hover:scale-105
                           transition-all duration-300"
                >
                  <MagnifyingGlassIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* City Selection - Desktop Only */}
            <div className="hidden sm:block overflow-x-auto pb-2">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-2 justify-center"
              >
                {cities.map((city) => (
                  <motion.button
                    key={city}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCity(city)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                              ${selectedCity === city 
                                ? 'bg-gradient-to-r from-[#FFD700] to-[#98FB98] text-gray-900' 
                                : 'bg-white/10 text-white hover:bg-white/20'}`}
                  >
                    {city}
                  </motion.button>
                ))}
              </motion.div>
            </div>
          </form>
        </motion.div>

        {/* Results Section */}
        <AnimatePresence mode="wait">
          {foodItems.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-7xl mx-auto"
            >
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Search Results</h2>
                  <span className="text-sm text-gray-400">
                    {selectedCity} • {foodItems.length} item{foodItems.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {foodItems.map((item) => (
                  <motion.div
                    key={item.id}
                    whileHover={{ scale: 1.02 }}
                    className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl overflow-hidden
                             border border-white/10 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="relative h-40 sm:h-48">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <button className="absolute top-2 right-2 sm:top-4 sm:right-4 p-1.5 sm:p-2 
                                       rounded-full bg-white/10 backdrop-blur-sm
                                       hover:bg-white/20 transition-all duration-200">
                        <HeartIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </button>
                    </div>

                    <div className="p-3 sm:p-4">
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <h3 className="font-semibold text-white text-sm sm:text-lg leading-tight line-clamp-2">
                          {item.name}
                        </h3>
                        <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-white/10 
                                     text-yellow-400 text-xs sm:text-sm font-medium whitespace-nowrap">
                          £{item.price.toFixed(2)}
                        </span>
                      </div>

                      <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2">
                        {item.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400" />
                          <span className="text-xs sm:text-sm text-gray-400">{item.overallRating}</span>
                        </div>
                        <span className="text-xs sm:text-sm text-red-400 truncate max-w-[120px]" title={item.restaurant_name}>
                          {item.restaurant_name}
                        </span>
                      </div>

                      <Link
                        to={`/food/restaurant/${item.restaurant_id}`}
                        state={{ focusedItemId: item.id }}
                        className="mt-3 sm:mt-4 w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl 
                                 font-medium text-xs sm:text-sm
                                 bg-gradient-to-r from-[#FFD700] to-[#98FB98]
                                 text-gray-900 hover:shadow-lg hover:from-[#FFD700] 
                                 hover:to-[#90EE90] transition-all duration-200
                                 flex items-center justify-center"
                      >
                        Order Now
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : isLoading ? (
            <div className="flex justify-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 
                            border-t-2 border-b-2 border-[#FFD700]"></div>
            </div>
          ) : searchQuery.trim() !== '' && !isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-8 sm:py-12 text-sm sm:text-base text-gray-400 px-3"
            >
              No results found for "{searchQuery}" in {selectedCity}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Food;
